import React, { useEffect, useState } from "react"
import { Btn, FormInput, FormSelect, FormTextarea, Loader, Modal } from "./";
import { useCore } from "../hooks";
import { Address, Customer } from "depoto-core";
import { usePaginatedCustomers } from "../utils/hooks/usePaginatedCustomers";
import { Schema } from "../utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useGQL } from "../utils/GQLContext.ts";
import { useConfirmationModal } from "../utils/hooks/useConfirmationModal.tsx";
import { initOrder } from "../store/core/reducer.ts";
import { faUserPen } from '@fortawesome/pro-regular-svg-icons'

type Props = {
  onExistingOrderCustomerChange?: (c: Customer | null) => void
  customer?: Customer
}

export const ModalCustomer: React.FC<Props> = ({ onExistingOrderCustomerChange, customer: customerProp }) => {
  const { currentOrder, setCurrentOrder, countries } = useCore()
  const activeCustomer = customerProp ?? (!onExistingOrderCustomerChange ? currentOrder?.customer : undefined)
  const [isVisible, setIsVisible] = useState<boolean>(false)
  const [isCreating, setIsCreating] = useState<boolean>(false)
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [searchPhrase, setSearchPhrase] = useState<string>('')
  const [fulltext, setFulltext] = useState<string>('')
  const [customer, setCustomer] = useState<Customer>()
  const [address, setAddress] = useState<Address>()
  const gql = useGQL()
  const queryClient = useQueryClient()
  const { ConfirmationModalComponent, showConfirmationModal, isConfirmed: isCustomerDeletionConfirmed } = useConfirmationModal()

  const { customers, loading: areCustomersLoading } = usePaginatedCustomers({
    filters: {
      fulltext,
    },
    sort: 'firstName',
    direction: 'ASC',
    schema: Schema.customer.detail,
  })

  const { mutate: mutationCreateAddress } = useMutation({
    mutationKey: ['createAddress'],
    mutationFn: (customerId: number) =>
      toast.promise(
        gql('mutation')({
          createAddress: [
            {
              firstName: customer.firstName,
              lastName: customer.lastName,
              email: customer.email,
              phone: customer.phone,
              street: address.street,
              zip: address.zip,
              city: address.city,
              country: address.country,
              companyName: customer.companyName,
              note: customer.note,
              customer: customerId
            },
            {
              errors: true,
            },
          ],
        }),
        {
          pending: 'Vytvářím adresu...',
          error: 'Něco se pokazilo',
          success: 'Adresa vytvořena',
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      cancelEditMode()
      setSearchPhrase(customer.email)
      setFulltext(customer.email)
    },
  })

  const { mutate: mutationUpdateAddress } = useMutation({
    mutationKey: ['updateAddress'],
    mutationFn: (customerId: number) =>
      toast.promise(
        gql('mutation')({
          updateAddress: [
            {
              id: address.id,
              firstName: customer.firstName,
              lastName: customer.lastName,
              email: customer.email,
              phone: customer.phone,
              street: address.street,
              zip: address.zip,
              city: address.city,
              country: address.country,
              companyName: customer.companyName,
              note: customer.note,
              customer: customerId
            },
            {
              errors: true,
            },
          ],
        }),
        {
          pending: 'Upravuji adresu...',
          error: 'Něco se pokazilo',
          success: 'Adresa upravena',
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      cancelEditMode()
      setSearchPhrase(customer.email)
      setFulltext(customer.email)
    },
  })

  const { mutate: mutationCreateCustomer } = useMutation({
    mutationKey: ['createCustomer'],
    mutationFn: () =>
      toast.promise(
        gql('mutation')({
          createCustomer: [
            {
              firstName: customer.firstName,
              lastName: customer.lastName,
              email: customer.email,
              phone: customer.phone,
              birthday: customer.birthday,
              companyName: customer.companyName,
              note: customer.note,
            },
            { data: { id: true }, errors: true },
          ],
        }),
        {
          pending: 'Vytvářím zákazníka...',
          error: 'Něco se pokazilo',
          success: 'Zákazník vytvořen',
        },
      ),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      const customerId = data?.createCustomer?.data?.id
      if (customerId > 0) {
        mutationCreateAddress(customerId)
      }
    },
  })

  const { mutate: mutationUpdateCustomer } = useMutation({
    mutationKey: ['updateCustomer'],
    mutationFn: () =>
      toast.promise(
        gql('mutation')({
          updateCustomer: [
            {
              id: customer.id,
              firstName: customer.firstName,
              lastName: customer.lastName,
              email: customer.email,
              phone: customer.phone,
              birthday: customer.birthday,
              companyName: customer.companyName,
              note: customer.note,
            },
            { data: { id: true }, errors: true },
          ],
        }),
        {
          pending: 'Upravuji zákazníka...',
          error: 'Něco se pokazilo',
          success: 'Zákazník upraven',
        },
      ),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      const customerId = data?.updateCustomer?.data?.id
      if (customerId > 0) {
        mutationUpdateAddress(customerId)
      }
    },
  })

  const { mutate: mutationDeleteCustomer } = useMutation({
    mutationKey: ['deleteCustomer'],
    mutationFn: () =>
      toast.promise(
        gql('mutation')({
          deleteCustomer: [
            {
              id: customer.id,
            },
            { errors: true },
          ],
        }),
        {
          pending: 'Odebírám zákazníka...',
          error: 'Něco se pokazilo',
          success: 'Zákazník odebrán',
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      cancelEditMode()
      setSearchPhrase('')
      setFulltext('')
    },
  })

  const openModal = () => {
    setIsVisible(true)
  }

  const closeModal = () => {
    setSearchPhrase('')
    setIsCreating(false)
    setIsEditing(false)
    setIsVisible(false)
  }

  const searchForCustomer = () => {
    setFulltext(searchPhrase)
  }

  const editNewCustomer = () => {
    setCustomer(new Customer())
    setAddress(new Address({ country: 'CZ' }))
    setIsCreating(true)
    setIsEditing(true)
  }

  const cancelEditMode = () => {
    setIsCreating(false)
    setIsEditing(false)
  }

  const editExistingCustomer = (c: Customer) => {
    setCustomer({ ...c, birthday: c.birthday?.substr(0, 10) })
    setAddress(c.addresses?.length ? c.addresses[0] : new Address())
    setIsEditing(true)
  }

  const editCustomerProperty = (key: string, val: string) => {
    const nextCustomer = { ...customer }
    nextCustomer[key] = val
    setCustomer(nextCustomer)
  }

  const editAddressProperty = (key: string, val: string) => {
    const nextAddress = { ...address }
    nextAddress[key] = val
    setAddress(nextAddress)
  }

  const createCustomer = () => {
    mutationCreateCustomer()
  }

  const updateCustomer = () => {
    mutationUpdateCustomer()
  }

  const deleteCustomer = () => {
    showConfirmationModal()
  }

  const clearCustomerFromOrder = () => {
    const nextOrder = { ...currentOrder || initOrder() }
    nextOrder.customer = undefined
    setCurrentOrder(nextOrder)
  }

  useEffect(() => {
    if (isCustomerDeletionConfirmed) {
      mutationDeleteCustomer()
    }
  }, [isCustomerDeletionConfirmed])

  const selectCustomer = (c: Customer) => {
    if (onExistingOrderCustomerChange) {
      onExistingOrderCustomerChange(c)
    } else {
      const nextOrder = { ...currentOrder || initOrder() }
      nextOrder.customer = c
      setCurrentOrder(nextOrder)
    }
    closeModal()
  }

  return (
    <>
      <Modal
        modalOpenBtn={
          <div className="flex flex-row items-center gap-2">
            {activeCustomer && (
              <span>
                {`${activeCustomer.firstName || ''} ${activeCustomer.lastName || ''}`.trim() ||
                  activeCustomer.email}
              </span>
            )}
            <div className="flex flex-row items-center gap-1">
              <Btn
                className="btn-base-100"
                icon={activeCustomer ? faUserPen : 'far fa-user-plus'}
                children={activeCustomer ? undefined : 'Vybrat zákazníka'}
                onClick={openModal}
              />
              {!onExistingOrderCustomerChange && activeCustomer && (
                <Btn
                  className="btn-base-100"
                  icon={'fa-trash'}
                  onClick={clearCustomerFromOrder}
                />
              )}
            </div>
          </div>
        }
        isVisible={isVisible}
        modalTitle={'Přidat zákazníka'}
        modalWidth={isEditing ? 'xl' : 'lg'}
        recalcHeightHash={isEditing ? +new Date() : +new Date()}
        onClose={closeModal}
        headerBtn={isEditing ? (
          <Btn className="btn-primary" icon={'fa-times'} children={'Zrušit'} onClick={cancelEditMode} />
        ) : (
          <Btn className="btn-primary" icon={'fa-plus'} children={'Přidat nového zákazníka'} onClick={editNewCustomer} />
        )}
        content={isEditing ? (
          <div className="flex flex-row">
            <div className="w-1/2">
              <FormInput label={'Jméno'} value={customer.firstName} onChange={val => editCustomerProperty('firstName', val)} />
              <FormInput label={'Příjmení'} value={customer.lastName} onChange={val => editCustomerProperty('lastName', val)} />
              <FormInput label={'Email'} value={customer.email} onChange={val => editCustomerProperty('email', val)} />
              <FormInput label={'Telefon'} value={customer.phone} onChange={val => editCustomerProperty('phone', val)} />
              <FormInput label={'Datum narození'} type={'date'} value={customer.birthday} onChange={val => editCustomerProperty('birthday', val)} />
            </div>
            <div className="w-1/2">
              <FormInput label={'Společnost'} value={customer.companyName} onChange={val => editCustomerProperty('companyName', val)} />
              <FormInput label={'Ulice a číslo popisné / orientační'} value={address.street} onChange={val => editAddressProperty('street', val)} />
              <div className="flex flex-row">
                <FormInput label={'PSČ'} value={address.zip} onChange={val => editAddressProperty('zip', val)} />
                <FormInput label={'Město'} value={address.city} onChange={val => editAddressProperty('city', val)} />
              </div>
              <FormSelect label={'Stát'} value={address.country} options={countries.map(c => ({ label: c.name, value: c.code }))} onChange={val => editAddressProperty('country', val)} />
              <FormTextarea label={'Poznámka'} value={customer.note} onChange={val => editCustomerProperty('note', val)} />
            </div>
          </div>
        ) : (
          <div>
            <div className="flex flex-row justify-between p-4">
              <FormInput className="grow pr-2" inputclassName="w-full" value={searchPhrase} onChange={val => setSearchPhrase(val)} onSubmit={searchForCustomer} />
              <Btn className="btn-primary" icon={'fa-search'} children={'Hledat'} onClick={searchForCustomer} />
            </div>
            <div className="h-modal-content-scrollable p-4">
              {customers.length > 0 ? customers.map(c => (
                <div className="flex flex-row items-center justify-start border-b-base-300 border-b-1 py-2" key={`customer-${c.id}`}>
                  <div className="avatar avatar-placeholder">
                    <div className="bg-light-blue text-primary font-bold uppercase text-base-content w-12 rounded-full mr-2">
                      <span>{c.firstName.charAt(0)}{c.lastName.charAt(0)}</span>
                    </div>
                  </div>
                  <div className="flex flex-col pr-2">
                    <div className="font-semibold text-sm">{c.firstName} {c.lastName}</div>
                    <div>{c.email}</div>
                  </div>
                  <div className="flex flex-row ml-auto">
                    <Btn className="btn-base-100 mr-2" icon={faUserPen} onClick={() => editExistingCustomer(c)} />
                    <Btn className="btn-primary" icon={'fa-user-plus'} onClick={() => selectCustomer(c)} />
                  </div>
                </div>
              )) : areCustomersLoading ? <Loader /> : <div>Nic nenalezeno</div>}
            </div>
          </div>
        )
        }
        footerContent={isEditing && (
          <div className="flex flex-row justify-between items-center">
            {isCreating ? (
              <div></div>
            ) : (
              <Btn className="btn-danger" icon={'fa-trash'} children={'Smazat'} onClick={deleteCustomer} />
            )}
            {isCreating ? (
              <Btn className="btn-primary" icon={'fa-check'} children={'Vytvořit zákazníka'} onClick={createCustomer} />
            ) : (
              <Btn className="btn-primary" icon={'fa-check'} children={'Upravit zákazníka'} onClick={updateCustomer} />
            )}
          </div>
        )
        }
      />
      <ConfirmationModalComponent title={'Opravdu si přejete smazat zákazníka?'} confirmBtnLabel={'Smazat zákazníka'} />
    </>
  )
}
